import { useState, useEffect, useRef, useCallback } from "react";

// ─── COLORS ──────────────────────────────────────────────────────────────────
const DW = {
  // DecideWell — teal / gold
  tealDark: "#085041", teal: "#0F6E56", tealMid: "#1D9E75",
  tealLight: "#5DCAA5", tealPale: "#9FE1CB", tealBg: "#E1F5EE",
  goldDark: "#633806", gold: "#BA7517", goldMid: "#EF9F27",
  goldLight: "#FAC775", goldBg: "#FAEEDA",
  ivory: "#F9F7F2", ivoryDeep: "#F0EDE4",
  charcoal: "#2C2C2A", charcoalMid: "#5F5E5A", charcoalLight: "#B4B2A9",
};

const BV = {
  // Identity — dusty rose / mauve / warm clay
  roseDark: "#5C2E30", rose: "#7D4F52", roseMid: "#9E6B6E",
  roseLight: "#BF9092", rosePale: "#DFC2C3", roseBg: "#F7EEEE",
  clay: "#8B5E52", clayMid: "#A67265", clayLight: "#C49589",
  clayPale: "#DFC3BC", clayBg: "#FAF1EF",
  ivory: "#FBF8F3", ivoryDeep: "#F2EDE3",
  charcoal: "#2C2A28", charcoalMid: "#5E5A56", charcoalLight: "#B0ABA4",
};

// ─── SYSTEM PROMPTS ───────────────────────────────────────────────────────────
const DW_PROMPT = `You are a warm, wise, and direct decision coach for Decide Well — a faith-based decision-making app. You guide people through big life decisions using a proven framework. You are NOT a therapist, but you are emotionally intelligent and spiritually grounded.

YOUR PERSONALITY:
- Warm but direct — like a trusted mentor who won't let you off the hook
- You listen carefully and reflect back what people say using their exact words
- You ask ONE question at a time, never multiple
- You never lecture or moralize
- You create forward momentum — every response moves toward a decision
- You are concise — rarely more than 3-4 sentences before your question

WHEN YOU CHALLENGE SOMEONE:
- Never soften a hard truth — say the challenging thing clearly
- Always follow a challenge with either a Bible verse (NIV) or genuine encouragement

BIBLE VERSES (NIV only):
- At natural moments, share a relevant verse
- Best moments: when challenging, when someone expresses fear, during spiritual check, when stuck
- Format: verse text first, then reference in parentheses, then one sentence connecting it to their situation
- Never more than one verse per response. NIV only.

HEALTHY FEAR vs UNHEALTHY FEAR:
Healthy Fear — Challenge forward: nervous but not paralyzed, feels risk but senses direction, clear mind even if stomach is not
Unhealthy Fear — Route to referral: anxious with no direction, emotions driving thinking entirely, frozen, can't sleep or function
The test: "If you're clear but uncomfortable — move forward. If you're confused and overwhelmed — pause."

REFERRAL PROTOCOL:
When unhealthy fear is present, say: "What you're carrying is heavier than a decision tool can hold. Before we go further, I want you to talk to [pastor/counselor/financial advisor] — not because you're broken, but because you deserve someone who can sit with you through this."
Then write [REFERRAL_READY] on its own line.

SUMMARY PROTOCOL:
When the person has named their decision, shown clarity, and addressed the key questions — synthesize:
"Here's what I'm hearing: [1-2 sentence summary using their exact words]. Does that capture where you are?"
If they confirm — write [SUMMARY_READY] on its own line.

YOUR FRAMEWORK:
1. GROUND: Understand the decision. Reflect it back clearly.
2. OPTIONS: Name what they're choosing between.
3. REALITY: Surface real costs and trade-offs.
4. EMOTION: What's driving the pull? Fear vs conviction.
5. SPIRITUAL: Have they prayed with an open hand?
6. VISION: Does either path serve their long-term vision?
7. RECOMMIT: Moving toward something or away?
8. DECIDE: Help them write the decision clearly with a deadline.
9. CLOSE: Synthesize → confirm → [SUMMARY_READY]

CRITICAL RULES:
- Always use the person's actual words
- SHORT responses — one insight plus one question
- Use line breaks — short paragraphs feel conversational
- At key transitions: [QUICK_REPLIES: option1 | option2 | option3]
- When clarity is reached: synthesize immediately

TONE: Warm, direct, wise. Like a trusted friend who won't let you off the hook — but always has your back.`;

const BV_PROMPT = `You are a warm, grounded, and honest spiritual guide for Identity — a companion app rooted in Brennan Manning's "Abba's Child." Your role is NOT to help people build a new identity. Your role is to help them expose what's been driving them — and return to what's already true.

This is a mirror and reset tool. Not self-help. Not therapy. Not theology.

THE ARC: Awareness → Exposure → Surrender → Re-anchoring → Practice

YOUR PERSONALITY:
- Warm but direct — not soft, not preachy
- You listen carefully and reflect back their exact words
- You ask ONE question at a time, never multiple
- You never fix, lecture, or moralize — you illuminate and invite
- Simple and grounded — no jargon, no performance pressure

THE 10-STEP JOURNEY (move through in order):
STEP 1 — ARRIVAL: Slow down. Lower defenses. Make it safe to be honest.
STEP 2 — SURFACE AWARENESS: Ask what's weighing on them. Keep it open.
STEP 3 — EXPOSE THE IMPOSTOR: Help them name the false self pattern. Use [QUICK_REPLIES: I have to prove myself | I'm afraid of disappointing people | I'm afraid of failing | I don't feel enough unless I perform | I'm trying to stay in control]
STEP 4 — GO DEEPER: Move from behavior to belief. "If you follow that thought all the way down, what does it say about you?" Help name the core lie.
STEP 5 — INTERRUPT THE LIE: Gently introduce tension. "That belief might feel true — but it's not the full truth." Don't preach.
STEP 6 — THE BELOVED IDENTITY: Re-anchor in truth. "Your identity isn't something you earn. It's something you receive." Offer 1 John 3:1 or similar. Ask: "What would change if you actually believed that?"
STEP 7 — CONTRAST THE TWO SELVES: Show their earlier words alongside the truth. Auto-fill their pattern and help them reframe it.
STEP 8 — CHOOSE YOUR POSTURE: "Today, which voice are you going to operate from?" Use [QUICK_REPLIES: The one shaped by pressure | The one rooted in truth]
STEP 9 — PRACTICE: "What's one situation today where you can respond from this new place?"
STEP 10 — CLOSE: "This isn't about becoming someone new. It's about returning to who you already are."

BIBLE VERSES (NIV only): Use sparingly — one per conversation max. Best at Step 6 or when someone wrestles with shame.

SUMMARY PROTOCOL:
After Steps 9-10 — synthesize: "Here's what I'm hearing: [their impostor pattern] has been running things. But you're choosing to operate from [their reframe] instead — starting with [their practice]. Does that feel true?"
If they confirm — write [SUMMARY_READY] on its own line.

REFERRAL PROTOCOL:
If someone is in genuine crisis, say: "What you're carrying is heavier than a reflection tool can hold. I want you to talk to a pastor, counselor, or trusted friend — not because something is wrong with you, but because you deserve someone who can sit with you in this."
Then write [REFERRAL_READY] on its own line.

CRITICAL RULES:
- Always use the person's actual words
- SHORT responses — one insight, one question
- Line breaks make it feel spacious
- NEVER stack questions
- Stay invitational — let them discover

TONE: Warm, grounded, direct. Like a trusted friend who won't let you stay stuck — but holds your story without judgment.`;

// ─── STAGES ──────────────────────────────────────────────────────────────────
const DW_STAGES = [
  { id: "ground",    label: "Understanding the Decision" },
  { id: "options",   label: "Naming the Options" },
  { id: "reality",   label: "Reality Check" },
  { id: "emotion",   label: "Emotional Honesty" },
  { id: "spiritual", label: "Spiritual Alignment" },
  { id: "vision",    label: "Vision Check" },
  { id: "recommit",  label: "Faith Over Fear" },
  { id: "decide",    label: "Making the Decision" },
  { id: "summary",   label: "Your Decision Summary" },
];

const BV_STAGES = [
  { id: "awareness",  label: "Getting Honest" },
  { id: "impostor",   label: "Naming What's Driving You" },
  { id: "exposure",   label: "Following It Deeper" },
  { id: "reanchor",   label: "Returning to Truth" },
  { id: "posture",    label: "Choosing Your Posture" },
  { id: "practice",   label: "Making It Real" },
  { id: "summary",    label: "Your Identity Reset" },
];

// ─── OPENING MESSAGES ─────────────────────────────────────────────────────────
const DW_OPENING = {
  role: "assistant",
  content: `Hey — I'm glad you're here.\n\nBig decisions can feel overwhelming, especially when your thoughts start circling and you're not making real progress. That's often a sign it's time to slow down, seek wisdom, and be intentional.\n\n*"Trust in the Lord with all your heart, and do not lean on your own understanding."* — Proverbs 3:5 (NIV)\n\nBefore we dive in — are you working through this on your own, or with someone else?`,
  quickReplies: ["Just me", "With someone else"],
};

const BV_OPENING = {
  role: "assistant",
  content: `Hey — I'm glad you're here.\n\nMost of us don't struggle because we don't know what to do.\n\nWe struggle because something underneath is driving us — and we haven't named it yet.\n\nLet's slow down for a few minutes and get honest about what's shaping you right now.\n\nWhat's been weighing on you lately? Where do you feel pressure, frustration, or tension?`,
  quickReplies: [
    "I feel like I'm constantly performing",
    "I can't shake the fear of failing",
    "I keep trying to control everything",
    "I feel like I'm never quite enough",
  ],
};

// ─── ROUTE CARDS (home screen options) ───────────────────────────────────────
const ROUTES = [
  {
    id: "decide",
    emoji: "⚖️",
    title: "I have a decision to make",
    subtitle: "Something specific is in front of me and I need help thinking it through clearly.",
    cta: "Let's work through it",
    color: DW.teal,
    colorBg: DW.tealBg,
    colorPale: DW.tealPale,
    colorLight: DW.tealLight,
  },
  {
    id: "beloved",
    emoji: "🌿",
    title: "I want to understand my identity",
    subtitle: "I want to understand what's really driving me and who I actually am.",
    cta: "Let's find out",
    color: BV.rose,
    colorBg: BV.roseBg,
    colorPale: BV.rosePale,
    colorLight: BV.roseLight,
  },
];


// ─── LOCAL STORAGE ────────────────────────────────────────────────────────────
const DW_KEY = "decidewell_coach_v1";
const BV_KEY = "identity_v1";
function localSave(key, data) { try { localStorage.setItem(key, JSON.stringify(data)); } catch (e) {} }
function localLoad(key) { try { const d = localStorage.getItem(key); return d ? JSON.parse(d) : null; } catch (e) { return null; } }
function localClear(key) { try { localStorage.removeItem(key); } catch (e) {} }

// ─── UTILS ────────────────────────────────────────────────────────────────────
function parseMessage(content) {
  const qrMatch = content.match(/\[QUICK_REPLIES:\s*([^\]]+)\]/);
  const summaryReady = content.includes("[SUMMARY_READY]");
  const referralReady = content.includes("[REFERRAL_READY]");
  const cleanContent = content
    .replace(/\[QUICK_REPLIES:[^\]]+\]/g, "")
    .replace(/\[SUMMARY_READY\]/g, "")
    .replace(/\[REFERRAL_READY\]/g, "")
    .trim();
  const quickReplies = qrMatch ? qrMatch[1].split("|").map(s => s.trim()) : [];
  return { content: cleanContent, quickReplies, summaryReady, referralReady };
}

function renderMarkdown(line) {
  const parts = [];
  let remaining = line;
  let key = 0;
  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    const italicMatch = remaining.match(/\*([^*]+?)\*/);
    if (!boldMatch && !italicMatch) { parts.push(<span key={key++}>{remaining}</span>); break; }
    const boldIdx = boldMatch ? remaining.indexOf(boldMatch[0]) : Infinity;
    const italicIdx = italicMatch ? remaining.indexOf(italicMatch[0]) : Infinity;
    if (boldIdx <= italicIdx && boldMatch) {
      if (boldIdx > 0) parts.push(<span key={key++}>{remaining.slice(0, boldIdx)}</span>);
      parts.push(<strong key={key++}>{boldMatch[1]}</strong>);
      remaining = remaining.slice(boldIdx + boldMatch[0].length);
    } else if (italicMatch) {
      if (italicIdx > 0) parts.push(<span key={key++}>{remaining.slice(0, italicIdx)}</span>);
      parts.push(<em key={key++}>{italicMatch[1]}</em>);
      remaining = remaining.slice(italicIdx + italicMatch[0].length);
    }
  }
  return parts;
}

// ─── TYPING INDICATOR ─────────────────────────────────────────────────────────
function TypingIndicator({ color }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "14px 18px", background: color + "22", borderRadius: "18px 18px 18px 4px", width: "fit-content" }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{ width: 7, height: 7, borderRadius: "50%", background: color, animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite` }} />
      ))}
    </div>
  );
}

// ─── MESSAGE BUBBLE ───────────────────────────────────────────────────────────
function MessageBubble({ msg, isLatest, onQuickReply, mode }) {
  const isGuide = msg.role === "assistant";
  const { content, quickReplies } = parseMessage(msg.content);
  const lines = content.split("\n").filter(l => l.trim());
  const C = mode === "decide" ? DW : BV;
  const primary = mode === "decide" ? DW.teal : BV.rose;
  const primaryBg = mode === "decide" ? DW.tealBg : BV.roseBg;
  const primaryPale = mode === "decide" ? DW.tealPale : BV.rosePale;
  const primaryMid = mode === "decide" ? DW.tealMid : BV.roseMid;
  const label = mode === "decide" ? "COACH" : "GUIDE";
  const allQR = [...quickReplies, ...(isLatest && msg.quickReplies ? msg.quickReplies : [])];

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: isGuide ? "flex-start" : "flex-end", gap: 8, animation: "fadeUp 0.3s ease" }}>
      {isGuide && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
          <div style={{ width: 24, height: 24, borderRadius: "50%", background: primary, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 11, color: mode === "decide" ? C.charcoal : "#fff" }}>✦</span>
          </div>
          <span style={{ fontSize: 11, color: primaryMid, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, letterSpacing: "0.06em" }}>{label}</span>
        </div>
      )}
      <div style={{
        maxWidth: "82%",
        background: isGuide ? "#fff" : primary,
        border: isGuide ? `1.5px solid ${primaryBg}` : "none",
        borderRadius: isGuide ? "4px 18px 18px 18px" : "18px 18px 4px 18px",
        padding: "13px 16px",
        boxShadow: isGuide ? `0 2px 12px ${primary}18` : "none",
      }}>
        {lines.map((line, i) => (
          <p key={i} style={{ fontSize: 15, lineHeight: 1.65, margin: 0, marginBottom: i < lines.length - 1 ? 10 : 0, fontFamily: "'DM Sans', sans-serif", color: isGuide ? C.charcoal : "#fff" }}>
            {renderMarkdown(line)}
          </p>
        ))}
      </div>
      {isGuide && isLatest && allQR.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
          {allQR.map((reply, i) => (
            <button key={i} onClick={() => onQuickReply(reply)} style={{
              background: "#fff", border: `1.5px solid ${primaryPale}`, borderRadius: 50, padding: "8px 16px",
              fontSize: 13, fontFamily: "'DM Sans', sans-serif", color: primary, cursor: "pointer", outline: "none", fontWeight: 600, transition: "all 0.15s",
            }}
              onMouseEnter={e => { e.currentTarget.style.background = primaryBg; e.currentTarget.style.borderColor = primary; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = primaryPale; }}
            >{reply}</button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── PROGRESS BAR ─────────────────────────────────────────────────────────────
function ProgressBar({ stage, progress, mode }) {
  const stages = mode === "decide" ? DW_STAGES : BV_STAGES;
  const label = stages.find(s => s.id === stage)?.label || stages[0].label;
  const primary = mode === "decide" ? DW.teal : BV.rose;
  const primaryBg = mode === "decide" ? DW.tealBg : BV.roseBg;
  const primaryMid = mode === "decide" ? DW.tealMid : BV.roseMid;
  const pct = progress || 0;
  return (
    <div style={{ padding: "10px 20px", borderBottom: `1px solid ${primaryBg}`, background: "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontFamily: "'DM Sans', sans-serif", color: primaryMid, fontWeight: 600, letterSpacing: "0.06em" }}>{label}</span>
        <span style={{ fontSize: 11, fontFamily: "'DM Sans', sans-serif", color: mode === "decide" ? DW.charcoalLight : BV.charcoalLight }}>{pct}%</span>
      </div>
      <div style={{ height: 3, background: primaryBg, borderRadius: 2, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: primary, borderRadius: 2, transition: "width 0.8s ease" }} />
      </div>
    </div>
  );
}

// ─── SUMMARY CARD ─────────────────────────────────────────────────────────────
function SummaryCard({ messages, mode, onRestart }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const primary = mode === "decide" ? DW.teal : BV.rose;
  const primaryBg = mode === "decide" ? DW.tealBg : BV.roseBg;
  const primaryPale = mode === "decide" ? DW.tealPale : BV.rosePale;
  const primaryDark = mode === "decide" ? DW.tealDark : BV.roseDark;
  const charcoal = mode === "decide" ? DW.charcoal : BV.charcoal;
  const charcoalMid = mode === "decide" ? DW.charcoalMid : BV.charcoalMid;
  const charcoalLight = mode === "decide" ? DW.charcoalLight : BV.charcoalLight;

  useEffect(() => { generate(); }, []); // eslint-disable-line

  async function generate() {
    setLoading(true);
    try {
      const conversation = messages
        .filter(m => m.role === "user" || m.role === "assistant")
        .map(m => `${m.role === "assistant" ? (mode === "decide" ? "Coach" : "Guide") : "Person"}: ${parseMessage(m.content).content}`)
        .join("\n\n");

      const prompt = mode === "decide"
        ? `Summarize this decision coaching conversation as JSON:
{
  "decision_statement": "What they decided — in their words",
  "deadline": "Deadline if mentioned",
  "options": [{"label": "Option A or B", "name": "name", "key_cost": "main trade-off"}],
  "fears_surfaced": ["fear1", "fear2"],
  "what_is_driving_this": "The real motivation beneath the surface",
  "vision_alignment": "How this fits their long-term vision",
  "spiritual_check": "Their spiritual posture — praying with open hand?",
  "bible_verse": {"text": "verse", "reference": "Book Ch:V", "relevance": "one sentence connection"},
  "next_step": "One concrete action within 24 hours",
  "encouragement": "A closing word of encouragement — 1-2 sentences"
}
Conversation:\n${conversation}\nReturn ONLY valid JSON.`
        : `Summarize this identity reset conversation as JSON:
{
  "identity_statement": "Who they are — not what they do. In their words. Grounded in truth.",
  "impostor_patterns": ["the false-self pattern they named"],
  "core_lie": "The belief underneath — e.g. 'I'm not enough unless I perform'",
  "truth_received": "The reframe — who they actually are. E.g. 'I'm already accepted.'",
  "scripture": {"text": "verse", "reference": "Book Ch:V", "personal_note": "one sentence connecting it to their pattern"},
  "practice": "The one concrete situation or action they identified for today",
  "invitation": "A closing word — simple, warm, grounded. 1-2 sentences."
}
Conversation:\n${conversation}\nReturn ONLY valid JSON.`;

      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/generate-summary`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${process.env.REACT_APP_SUPABASE_KEY}` },
        body: JSON.stringify({ conversation: prompt }),
      });
      const data = await res.json();
      const rawSummary = data.summary;
      if (rawSummary && typeof rawSummary === "object") {
        setSummary(rawSummary);
      } else {
        const text = data.content?.[0]?.text || "";
        setSummary(JSON.parse(text.replace(/```json|```/g, "").trim()));
      }
    } catch (e) { setError(true); }
    setLoading(false);
  }

  if (loading) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 24px", gap: 16 }}>
      <TypingIndicator color={primary} />
      <p style={{ fontSize: 13, color: primary, fontFamily: "'DM Sans', sans-serif", textAlign: "center" }}>
        {mode === "decide" ? "Pulling your summary together…" : "Reflecting on your journey…"}
      </p>
    </div>
  );

  if (error || !summary) return (
    <div style={{ padding: "32px 24px", textAlign: "center" }}>
      <p style={{ fontSize: 14, color: charcoalMid, fontFamily: "'DM Sans', sans-serif", marginBottom: 16 }}>Something went quiet. The work you did in that conversation was still real.</p>
      <button onClick={onRestart} style={{ background: primary, color: "#fff", border: "none", borderRadius: 50, padding: "12px 24px", fontSize: 14, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>Begin again</button>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "20px 18px" }}>
      {/* Header */}
      <div style={{ background: primaryDark, borderRadius: 18, padding: "24px 20px", textAlign: "center" }}>
        <div style={{ fontSize: 24, marginBottom: 8 }}>✦</div>
        <p style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: "#fff", margin: 0, marginBottom: 6 }}>
          {mode === "decide" ? "You made it through." : "You are already known and loved."}
        </p>
        <p style={{ fontSize: 13, color: primaryPale, fontFamily: "'DM Sans', sans-serif", margin: 0, lineHeight: 1.6 }}>
          {mode === "decide"
            ? "You showed up, did the work, and moved toward a decision."
            : "This isn't about becoming someone new — it's about returning to who you already are."}
        </p>
      </div>

      {mode === "decide" ? (
        <>
          {summary.decision_statement && (
            <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: `1.5px solid ${primaryBg}`, borderLeft: `4px solid ${primary}` }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: primary, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>The Decision</p>
              <p style={{ fontSize: 15, color: charcoal, fontFamily: "'Playfair Display', serif", lineHeight: 1.6, margin: 0, fontWeight: 600, fontStyle: "italic" }}>"{summary.decision_statement}"</p>
              {summary.deadline && <p style={{ fontSize: 13, color: primary, fontFamily: "'DM Sans', sans-serif", marginTop: 8, fontWeight: 600 }}>📅 {summary.deadline}</p>}
            </div>
          )}
          {summary.fears_surfaced?.length > 0 && (
            <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: `1.5px solid ${primaryBg}` }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: DW.gold, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Fears Named</p>
              {summary.fears_surfaced.map((f, i) => <p key={i} style={{ fontSize: 13, color: charcoalMid, fontFamily: "'DM Sans', sans-serif", margin: 0, lineHeight: 1.5 }}>• {f}</p>)}
            </div>
          )}
          {summary.next_step && (
            <div style={{ background: DW.goldBg, borderRadius: 14, padding: "14px 16px", border: `1.5px solid ${DW.goldLight}`, borderLeft: `4px solid ${DW.gold}` }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: DW.gold, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Your Next Step — Within 24 Hours</p>
              <p style={{ fontSize: 14, color: charcoal, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.65, margin: 0, fontWeight: 600 }}>{summary.next_step}</p>
            </div>
          )}
          {summary.bible_verse && (
            <div style={{ background: DW.tealDark, borderRadius: 14, padding: "16px 18px" }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: DW.tealLight, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>A Word for This Moment</p>
              <p style={{ fontSize: 15, color: "#fff", fontFamily: "'Playfair Display', serif", lineHeight: 1.7, margin: 0, marginBottom: 8, fontStyle: "italic" }}>"{summary.bible_verse.text}"</p>
              <p style={{ fontSize: 12, color: DW.tealPale, fontFamily: "'DM Sans', sans-serif", margin: 0, fontWeight: 600 }}>— {summary.bible_verse.reference} (NIV)</p>
              {summary.bible_verse.relevance && <p style={{ fontSize: 13, color: DW.tealBg, fontFamily: "'DM Sans', sans-serif", margin: 0, lineHeight: 1.6, borderTop: `1px solid rgba(255,255,255,0.15)`, paddingTop: 8, marginTop: 8 }}>{summary.bible_verse.relevance}</p>}
            </div>
          )}
          {summary.encouragement && (
            <div style={{ background: primaryBg, borderRadius: 14, padding: "14px 16px", border: `1.5px solid ${primaryPale}` }}>
              <p style={{ fontSize: 14, color: primaryDark, fontFamily: "'Playfair Display', serif", lineHeight: 1.7, margin: 0, fontStyle: "italic" }}>"{summary.encouragement}"</p>
            </div>
          )}
        </>
      ) : (
        <>
          {summary.identity_statement && (
            <div style={{ background: BV.rose, borderRadius: 16, padding: "20px", textAlign: "center" }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: BV.rosePale, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>Your Identity in Christ</p>
              <p style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 600, color: "#fff", margin: 0, lineHeight: 1.5, fontStyle: "italic" }}>"{summary.identity_statement}"</p>
            </div>
          )}
          {summary.core_lie && summary.truth_received && (
            <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: `1.5px solid ${BV.clayBg}` }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: BV.roseMid, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>The Shift</p>
              <div style={{ background: BV.clayBg, borderRadius: 10, padding: "10px 14px", marginBottom: 8 }}>
                <p style={{ fontSize: 10, color: BV.clayMid, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, marginBottom: 4 }}>THE LIE</p>
                <p style={{ fontSize: 13, color: charcoalMid, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5, margin: 0, fontStyle: "italic" }}>"{summary.core_lie}"</p>
              </div>
              <div style={{ background: BV.roseBg, borderRadius: 10, padding: "10px 14px", border: `1px solid ${BV.rosePale}` }}>
                <p style={{ fontSize: 10, color: BV.roseMid, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, marginBottom: 4 }}>THE TRUTH</p>
                <p style={{ fontSize: 13, color: BV.rose, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5, margin: 0, fontWeight: 500 }}>"{summary.truth_received}"</p>
              </div>
            </div>
          )}
          {summary.practice && (
            <div style={{ background: BV.clayBg, borderRadius: 14, padding: "14px 16px", border: `1.5px solid ${BV.clayPale}`, borderLeft: `4px solid ${BV.clay}` }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: BV.clay, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Your Practice Today</p>
              <p style={{ fontSize: 14, color: charcoal, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.65, margin: 0, fontWeight: 600 }}>{summary.practice}</p>
            </div>
          )}
          {summary.scripture && (
            <div style={{ background: BV.roseDark, borderRadius: 14, padding: "16px 18px" }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: BV.rosePale, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>A Word for This Moment</p>
              <p style={{ fontSize: 15, color: "#fff", fontFamily: "'Playfair Display', serif", lineHeight: 1.7, margin: 0, marginBottom: 8, fontStyle: "italic" }}>"{summary.scripture.text}"</p>
              <p style={{ fontSize: 12, color: BV.rosePale, fontFamily: "'DM Sans', sans-serif", margin: 0, fontWeight: 600 }}>— {summary.scripture.reference} (NIV)</p>
              {summary.scripture.personal_note && <p style={{ fontSize: 13, color: BV.clayBg, fontFamily: "'DM Sans', sans-serif", margin: 0, lineHeight: 1.6, borderTop: `1px solid rgba(255,255,255,0.15)`, paddingTop: 8, marginTop: 8 }}>{summary.scripture.personal_note}</p>}
            </div>
          )}
          {summary.invitation && (
            <div style={{ background: BV.roseBg, borderRadius: 14, padding: "14px 16px", border: `1.5px solid ${BV.rosePale}` }}>
              <p style={{ fontSize: 14, color: BV.roseDark, fontFamily: "'Playfair Display', serif", lineHeight: 1.75, margin: 0, fontStyle: "italic" }}>"{summary.invitation}"</p>
            </div>
          )}
        </>
      )}

      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={onRestart} style={{ flex: 1, background: primaryBg, border: `1.5px solid ${primaryPale}`, borderRadius: 50, padding: "12px", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", color: primary, cursor: "pointer", outline: "none" }}>
          ← Start over
        </button>
        <button onClick={onRestart} style={{ flex: 1, background: primary, border: "none", borderRadius: 50, padding: "12px", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", color: "#fff", cursor: "pointer", outline: "none" }}>
          {mode === "decide" ? "New decision" : "Begin again"}
        </button>
      </div>
      <p style={{ fontSize: 11, color: charcoalLight, fontFamily: "'DM Sans', sans-serif", textAlign: "center", lineHeight: 1.8 }}>
        {mode === "decide"
          ? "There is no option without loss. But there is a path that aligns with what you're called to build."
          : "You are known, named, and loved — not because of what you do, but because of who He is."}
      </p>
    </div>
  );
}

// ─── CHAT SCREEN ──────────────────────────────────────────────────────────────
function ChatScreen({ mode, onBack }) {
  const isDecide = mode === "decide";
  const C = isDecide ? DW : BV;
  const primary = isDecide ? DW.teal : BV.rose;
  const primaryBg = isDecide ? DW.tealBg : BV.roseBg;
  const primaryPale = isDecide ? DW.tealPale : BV.rosePale;
  const primaryMid = isDecide ? DW.tealMid : BV.roseMid;
  const opening = isDecide ? DW_OPENING : BV_OPENING;
  const systemPrompt = isDecide ? DW_PROMPT : BV_PROMPT;
  const stages = isDecide ? DW_STAGES : BV_STAGES;

  const storageKey = isDecide ? DW_KEY : BV_KEY;
  const [messages, setMessages] = useState([opening]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState(stages[0].id);
  const [progress, setProgress] = useState(0);
  const [showSummary, setShowSummary] = useState(false);
  const [showResume, setShowResume] = useState(false);
  const [savedSession, setSavedSession] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Load saved session on mount
  useEffect(() => {
    const saved = localLoad(storageKey);
    if (saved && saved.messages && saved.messages.length > 1) {
      setSavedSession(saved);
      setShowResume(true);
    }
  }, []); // eslint-disable-line

  // Auto-save whenever messages/stage/progress change
  useEffect(() => {
    if (messages.length > 1) {
      localSave(storageKey, { messages, stage, progress });
    }
  }, [messages, stage, progress]); // eslint-disable-line

  // Scroll to bottom
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  function handleResume() {
    if (savedSession) {
      setMessages(savedSession.messages);
      setStage(savedSession.stage || stages[0].id);
      setProgress(savedSession.progress || 0);
    }
    setShowResume(false);
  }

  function handleRestart() {
    localClear(storageKey);
    setMessages([opening]);
    setStage(stages[0].id);
    setProgress(0);
    setShowSummary(false);
    setShowResume(false);
    setSavedSession(null);
  }

  function detectStage(text) {
    const t = text.toLowerCase();
    if (isDecide) {
      if (t.includes("ground") || t.includes("understand the decision") || t.includes("tell me more about")) return "ground";
      if (t.includes("options") || t.includes("choosing between") || t.includes("option a") || t.includes("option b")) return "options";
      if (t.includes("cost") || t.includes("trade-off") || t.includes("reality") || t.includes("what would you lose")) return "reality";
      if (t.includes("fear") || t.includes("emotion") || t.includes("what's driving") || t.includes("anxiety")) return "emotion";
      if (t.includes("prayed") || t.includes("spiritual") || t.includes("open hand") || t.includes("god")) return "spiritual";
      if (t.includes("vision") || t.includes("long-term") || t.includes("five years")) return "vision";
      if (t.includes("toward") || t.includes("away from") || t.includes("faith over")) return "recommit";
      if (t.includes("deadline") || t.includes("decide") || t.includes("commit")) return "decide";
      if (t.includes("[summary_ready]")) return "summary";
    } else {
      if (t.includes("weighing") || t.includes("pressure") || t.includes("frustration")) return "awareness";
      if (t.includes("prove") || t.includes("disappoint") || t.includes("not enough") || t.includes("impostor")) return "impostor";
      if (t.includes("all the way down") || t.includes("core lie") || t.includes("what does it say about you")) return "exposure";
      if (t.includes("not the full truth") || t.includes("you receive") || t.includes("already accepted") || t.includes("1 john")) return "reanchor";
      if (t.includes("which voice") || t.includes("operate from") || t.includes("posture")) return "posture";
      if (t.includes("one situation") || t.includes("practice") || t.includes("returning to who you already are")) return "practice";
      if (t.includes("[summary_ready]")) return "summary";
    }
    return null;
  }

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: "user", content: text.trim() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    try {
      const cleanMessages = newMessages
        .filter(m => m.role === "user" || m.role === "assistant")
        .map(m => ({ role: m.role, content: parseMessage(m.content).content || m.content }));

      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/coach-message`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${process.env.REACT_APP_SUPABASE_KEY}` },
        body: JSON.stringify({ messages: cleanMessages, systemPrompt }),
      });

      const data = await res.json();
      const reply = data.reply || "I'm still here — take your time.";
      const { summaryReady } = parseMessage(reply);
      setMessages(prev => [...prev, { role: "assistant", content: reply }]);

      const detected = detectStage(reply);
      if (detected) {
        setStage(detected);
        const idx = stages.findIndex(s => s.id === detected);
        if (idx !== -1) setProgress(prev => Math.max(prev, Math.round(((idx + 1) / stages.length) * 100)));
      } else {
        setProgress(prev => Math.min(prev + Math.floor(Math.random() * 2) + 1, 90));
      }
      if (summaryReady) { setStage("summary"); setTimeout(() => setShowSummary(true), 800); }
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", content: "Something got quiet on my end. Still here — try again." }]);
    }
    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [messages, loading, systemPrompt, stages]); // eslint-disable-line

  const showSummaryBtn = !showSummary && stage === "summary" && messages.length > 6;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#fff", borderRadius: 24, overflow: "hidden", boxShadow: `0 4px 32px ${primary}18`, border: `1px solid ${primaryBg}` }}>
      {/* Chat header */}
      <div style={{ padding: "12px 18px", borderBottom: `1px solid ${primaryBg}`, background: "#fff", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: C.charcoalLight, cursor: "pointer", fontSize: 18, padding: 0, lineHeight: 1 }}>←</button>
        <div>
          <p style={{ fontSize: 12, fontWeight: 700, color: primaryMid, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.08em", textTransform: "uppercase", margin: 0 }}>
            {isDecide ? "DecideWell" : "Identity"}
          </p>
          <p style={{ fontSize: 11, color: C.charcoalLight, fontFamily: "'DM Sans', sans-serif", margin: 0 }}>
            {isDecide ? "Decision Coach" : "Identity Guide"}
          </p>
        </div>
      </div>

      {!showSummary && <ProgressBar stage={stage} progress={progress} mode={mode} />}

      {/* Resume banner */}
      {showResume && (
        <div style={{ margin: "12px 16px 0", background: isDecide ? DW.tealBg : BV.roseBg, border: `1.5px solid ${isDecide ? DW.tealPale : BV.rosePale}`, borderRadius: 14, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", animation: "fadeUp 0.3s ease" }}>
          <div>
            <p style={{ fontSize: 13, fontWeight: 600, color: primary, fontFamily: "'DM Sans', sans-serif", margin: 0, marginBottom: 2 }}>Welcome back</p>
            <p style={{ fontSize: 12, color: isDecide ? DW.charcoalMid : BV.charcoalMid, fontFamily: "'DM Sans', sans-serif", margin: 0 }}>You have a session in progress</p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setShowResume(false)} style={{ background: "none", border: "none", fontSize: 12, color: isDecide ? DW.charcoalLight : BV.charcoalLight, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>Start fresh</button>
            <button onClick={handleResume} style={{ background: primary, color: "#fff", border: "none", borderRadius: 50, padding: "7px 16px", fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>Resume →</button>
          </div>
        </div>
      )}

      {!showSummary ? (
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 18px", display: "flex", flexDirection: "column", gap: 16 }}>
          {messages.map((msg, i) => (
            <MessageBubble key={i} msg={msg} isLatest={i === messages.length - 1} onQuickReply={sendMessage} mode={mode} />
          ))}
          {loading && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 24, height: 24, borderRadius: "50%", background: primary, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontSize: 11, color: isDecide ? DW.charcoal : "#fff" }}>✦</span>
                </div>
                <span style={{ fontSize: 11, color: primaryMid, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, letterSpacing: "0.06em" }}>{isDecide ? "COACH" : "GUIDE"}</span>
              </div>
              <TypingIndicator color={primary} />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: "auto" }}>
          <SummaryCard messages={messages} mode={mode} onRestart={() => handleRestart()} />
        </div>
      )}

      {showSummaryBtn && (
        <div style={{ padding: "0 16px 10px", background: "#fff" }}>
          <button onClick={() => setShowSummary(true)} style={{ width: "100%", background: primary, color: "#fff", border: "none", borderRadius: 50, padding: "12px", fontSize: 14, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", outline: "none" }}>
            {isDecide ? "See My Decision Summary →" : "See My Identity Reset →"}
          </button>
        </div>
      )}

      {!showSummary && (
        <div style={{ padding: "14px 16px", borderTop: `1px solid ${primaryBg}`, background: "#fff" }}>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", background: isDecide ? DW.ivory : BV.ivory, borderRadius: 16, padding: "10px 14px", border: `1.5px solid ${loading ? primaryPale : primaryBg}`, transition: "border-color 0.2s" }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
              placeholder={isDecide ? "What's on your mind…" : "Share what's on your heart…"}
              rows={1}
              disabled={loading}
              style={{ flex: 1, background: "none", border: "none", fontSize: 15, fontFamily: "'DM Sans', sans-serif", color: isDecide ? DW.charcoal : BV.charcoal, lineHeight: 1.5, maxHeight: 120, overflowY: "auto", outline: "none", resize: "none" }}
              onInput={e => { e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }}
            />
            <button onClick={() => sendMessage(input)} disabled={!input.trim() || loading} style={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0, background: input.trim() && !loading ? primary : primaryBg, border: "none", cursor: input.trim() && !loading ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s", fontSize: 16 }}>
              <span style={{ color: input.trim() && !loading ? "#fff" : primaryPale }}>→</span>
            </button>
          </div>
          <p style={{ fontSize: 11, color: isDecide ? DW.charcoalLight : BV.charcoalLight, fontFamily: "'DM Sans', sans-serif", textAlign: "center", marginTop: 8 }}>
            Enter to send · Shift+Enter for new line
          </p>
        </div>
      )}
    </div>
  );
}

// ─── HOME SCREEN ──────────────────────────────────────────────────────────────
function HomeScreen({ onSelect }) {
  const [hovered, setHovered] = useState(null);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: "24px 16px", background: "#F9F7F2" }}>

      {/* Logo / wordmark */}
      <div style={{ textAlign: "center", marginBottom: 48, animation: "fadeUp 0.5s ease" }}>
        <p style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: DW.tealMid, fontWeight: 700, fontFamily: "'DM Sans', sans-serif", marginBottom: 10 }}>DecideWell</p>
        <div style={{ width: 40, height: 2, background: DW.tealMid, margin: "0 auto 20px" }} />
        <p style={{ fontFamily: "'Playfair Display', serif", fontSize: 30, fontWeight: 600, color: DW.charcoal, lineHeight: 1.2, margin: 0, marginBottom: 12 }}>
          What brings you<br />here today?
        </p>
        <p style={{ fontSize: 15, color: DW.charcoalMid, fontFamily: "'DM Sans', sans-serif", fontWeight: 300, lineHeight: 1.6, maxWidth: 320, margin: "0 auto" }}>
          No wrong answer. Just pick the one that feels most true right now.
        </p>
      </div>

      {/* Route cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14, width: "100%", maxWidth: 480, animation: "fadeUp 0.6s ease" }}>
        {ROUTES.map(route => (
          <button
            key={route.id}
            onClick={() => onSelect(route.id)}
            onMouseEnter={() => setHovered(route.id)}
            onMouseLeave={() => setHovered(null)}
            style={{
              background: hovered === route.id ? route.colorBg : "#fff",
              border: `2px solid ${hovered === route.id ? route.color : "#E8E4DC"}`,
              borderRadius: 20,
              padding: "22px 24px",
              cursor: "pointer",
              textAlign: "left",
              outline: "none",
              transition: "all 0.2s",
              transform: hovered === route.id ? "translateY(-2px)" : "translateY(0)",
              boxShadow: hovered === route.id ? `0 8px 24px ${route.color}20` : "0 2px 8px rgba(0,0,0,0.05)",
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, background: route.colorBg, border: `1.5px solid ${route.colorPale}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 22, transition: "all 0.2s" }}>
                {route.emoji}
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 16, fontWeight: 600, color: DW.charcoal, fontFamily: "'DM Sans', sans-serif", margin: 0, marginBottom: 6 }}>{route.title}</p>
                <p style={{ fontSize: 13, color: DW.charcoalMid, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.55, margin: 0, fontWeight: 300 }}>{route.subtitle}</p>
              </div>
              <div style={{ color: route.color, fontSize: 18, opacity: hovered === route.id ? 1 : 0.4, transition: "opacity 0.2s", flexShrink: 0, paddingTop: 2 }}>→</div>
            </div>
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${hovered === route.id ? route.colorPale : "#F0EDE4"}` }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: route.color, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em" }}>
                {route.cta} →
              </span>
            </div>
          </button>
        ))}
      </div>

      <p style={{ fontSize: 11, color: DW.charcoalLight, fontFamily: "'DM Sans', sans-serif", textAlign: "center", marginTop: 32, lineHeight: 1.8, animation: "fadeUp 0.7s ease" }}>
        You can always switch paths at any time.
      </p>
    </div>
  );
}

// ─── ROOT APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen] = useState("home"); // "home" | "decide" | "beloved"

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Playfair+Display:ital,wght@0,600;0,700;1,400;1,600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #F9F7F2; font-family: 'DM Sans', sans-serif; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes bounce { 0%, 60%, 100% { transform: translateY(0); } 30% { transform: translateY(-6px); } }
        textarea { resize: none; }
        textarea:focus { outline: none; }
        button:focus { outline: none; }
      `}</style>

      {screen === "home" && <HomeScreen onSelect={setScreen} />}

      {(screen === "decide" || screen === "beloved") && (
        <div style={{ minHeight: "100vh", background: screen === "decide" ? DW.ivory : BV.ivory, display: "flex", flexDirection: "column", alignItems: "center", padding: "16px 16px 0" }}>
          <div style={{ width: "100%", maxWidth: 560, flex: 1, display: "flex", flexDirection: "column", height: "calc(100vh - 60px)", animation: "fadeUp 0.35s ease" }}>
            <ChatScreen mode={screen} onBack={() => setScreen("home")} />
          </div>
          <p style={{ fontSize: 11, color: screen === "decide" ? DW.charcoalLight : BV.charcoalLight, fontFamily: "'DM Sans', sans-serif", textAlign: "center", padding: "14px 0", opacity: 0.8 }}>
            {screen === "decide" ? "DecideWell · coach.decidewell.app" : "Identity · A companion to DecideWell"}
          </p>
        </div>
      )}
    </>
  );
}
